import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, User as UserIcon, Building2, CheckCircle, Globe, Loader2, AlertCircle, Fingerprint, Eye, EyeOff, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { startAuthentication } from '@simplewebauthn/browser';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-hot-toast';
import { Club, api } from '../services/api';
import ClubSelector from './ClubSelector';
import AddressAutocomplete from './Shared/AddressAutocomplete';

interface AuthModalProps {
    isOpen: boolean;
    onClose: () => void;
    defaultMode?: 'login' | 'register' | 'admin-login';
    clubs?: Club[];
    onLogin: (mode: 'login' | 'register' | 'admin-login', email: string, pass: string, pseudo?: string, termsAccepted?: boolean, clubClaim?: { clubId: string, proofUrl: string }, newClubData?: { name: string, address: string, city: string, zip_code: string, country: string, website: string, proofUrl: string }, managerDetails?: { firstName: string, lastName: string }, role?: string) => Promise<void>;
    initialIsManager?: boolean;
    initialManagerType?: 'club' | 'shop' | 'federation';
}

const AuthModal = ({ isOpen, onClose, defaultMode = 'login', onLogin, clubs = [], initialIsManager = false, initialManagerType = 'club' }: AuthModalProps) => {
    const { t } = (useTranslation as any)('auth');
    const { login: authLogin } = useAuth();
    const [mode, setMode] = useState<'login' | 'register' | 'admin-login'>(defaultMode);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [pseudo, setPseudo] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [privacyAccepted, setPrivacyAccepted] = useState(false);
    const [internalClubs, setInternalClubs] = useState<Club[]>([]);

    // Manager Flow
    const [isManager, setIsManager] = useState(initialIsManager);
    const [managerType, setManagerType] = useState<'club' | 'shop' | 'federation'>(initialManagerType);
    const [showClubSelector, setShowClubSelector] = useState(false);
    const [selectedClub, setSelectedClub] = useState<Club | null>(null);
    const [proofs, setProofs] = useState<{ url: string, description: string }[]>([{ url: '', description: '' }]);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');

    // New Club Flow
    const [isNewClub, setIsNewClub] = useState(false);
    const [newClubName, setNewClubName] = useState('');
    const [newClubData, setNewClubData] = useState({
        address: '',
        city: '',
        zip: '',
        region: '',
        country: '',
        website: '',
        latitude: undefined as number | undefined,
        longitude: undefined as number | undefined
    });

    // Shop Flow
    const [shopName, setShopName] = useState('');
    const [isShopNameAvailable, setIsShopNameAvailable] = useState<boolean | null>(null);
    const [isCheckingShopName, setIsCheckingShopName] = useState(false);
    const [shopNameError, setShopNameError] = useState('');
    const [shopData, setShopData] = useState({
        address: '',
        city: '',
        zip: '',
        region: '',
        country: '',
        website_url: '',
        latitude: undefined as number | undefined,
        longitude: undefined as number | undefined
    });

    useEffect(() => {
        if (isOpen) {
            setMode(defaultMode);
            setIsManager(initialIsManager);
            setManagerType(initialManagerType);
            setError('');
            setShopName('');
            setIsShopNameAvailable(null);
            setShopNameError('');
            setShopData({
                address: '',
                city: '',
                zip: '',
                region: '',
                country: '',
                website_url: '',
                latitude: undefined,
                longitude: undefined
            });
        }
    }, [isOpen, defaultMode, initialIsManager, initialManagerType]);

    // Internal Fetch for Clubs if prop is empty
    useEffect(() => {
        if (isOpen && clubs.length === 0 && internalClubs.length === 0) {
            api.clubs.list()
                .then(setInternalClubs)
                .catch(err => console.error("AuthModal: Failed to load fallback clubs", err));
        }
    }, [isOpen, clubs.length, internalClubs.length]);

    // Debounced Shop Name Check
    useEffect(() => {
        if (!isManager || managerType !== 'shop' || shopName.trim().length < 3) {
            setIsShopNameAvailable(null);
            setShopNameError('');
            return;
        }

        const timer = setTimeout(async () => {
            setIsCheckingShopName(true);
            setShopNameError('');
            try {
                const { available } = await api.marketplace.checkShopName(shopName.trim());
                setIsShopNameAvailable(available);
                if (!available) {
                    setShopNameError(t('register.shop_name_taken'));
                }
            } catch (err) {
                console.error("Failed to check shop name:", err);
            } finally {
                setIsCheckingShopName(false);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [shopName, isManager, managerType, t]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            // Manual Validation for Password Length (HTML5 validation ignored due to type="button")
            if ((mode === 'register' || mode === 'login' || mode === 'admin-login') && password.length < 6) {
                throw new Error(t('register.error_password_length'));
            }
            if (mode === 'register' && password !== confirmPassword) {
                throw new Error(t('register.error_password_match'));
            }
            if (mode === 'register' && isManager && managerType === 'shop' && (!shopName || isShopNameAvailable === false)) {
                throw new Error(shopNameError || t('register.shop_name_placeholder'));
            }
            if (mode === 'register' && (!termsAccepted || !privacyAccepted)) {
                throw new Error(t('register.error_terms'));
            }
            // Pass true for termsAccepted if in register mode
            // Serialize proofs
            const proofString = JSON.stringify(proofs.filter(p => p.url.trim() !== ''));

            const clubClaim = (mode === 'register' && isManager && managerType === 'club' && selectedClub && !isNewClub)
                ? { clubId: selectedClub.id.toString(), proofUrl: proofString }
                : undefined;

            const newClubPayload = (mode === 'register' && isManager)
                ? (managerType === 'club' && isNewClub)
                    ? {
                        name: newClubName,
                        address: newClubData.address,
                        city: newClubData.city,
                        zip_code: newClubData.zip,
                        region: newClubData.region,
                        country: newClubData.country,
                        latitude: newClubData.latitude,
                        longitude: newClubData.longitude,
                        website: newClubData.website,
                        proofUrl: proofString
                    }
                    : (managerType === 'shop')
                        ? {
                            name: shopName,
                            address: shopData.address,
                            city: shopData.city,
                            zip_code: shopData.zip,
                            region: shopData.region,
                            country: shopData.country,
                            latitude: shopData.latitude,
                            longitude: shopData.longitude,
                            website: shopData.website_url,
                            proofUrl: ''
                        }
                        : undefined
                : undefined;

            const managerDetails = (mode === 'register')
                ? { firstName, lastName }
                : undefined;

            const role = isManager ? (managerType === 'club' ? 'club_manager' : 'merchant') : 'player';

            await onLogin(mode, email.trim(), password, pseudo.trim(), mode === 'register' && termsAccepted && privacyAccepted, clubClaim, newClubPayload, managerDetails, role);
            onClose();
        } catch (err: any) {
            console.error(err);
            setError(err.message || t('modal.error_auth'));
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const handlePasskeyLogin = async () => {
        setLoading(true);
        setError('');
        try {
            // 1. Get options from backend
            const options = await api.auth.getWebAuthnLoginOptions(email || undefined);

            // 2. Start authentication via browser
            const asseResp = await startAuthentication(options);

            // 3. Verify on backend
            const verifyResp = await api.auth.verifyWebAuthnLogin(asseResp, email || undefined);

            if (verifyResp.token) {
                authLogin(verifyResp.token, verifyResp.user);
                onClose();
                toast.success(t('login.success') || "Welcome!");
            }
        } catch (err: any) {
            console.error("Passkey Login Error:", err);
            setError(err.message || t('login.passkey_error'));
        } finally {
            setLoading(false);
        }
    };

    const handleSocialLogin = async (provider: 'google' | 'outlook' | 'facebook') => {
        setLoading(true);
        setError('');
        try {
            const redirectUri = window.location.origin + '/auth/callback';
            // Pass the intended role and provider in 'state'
            const signupRole = isManager ? (managerType === 'club' ? 'club_manager' : 'merchant') : 'player';
            const state = JSON.stringify({ signup_role: signupRole, provider });

            const { url } = await api.auth.getSocialAuthUrl(provider, redirectUri, state);
            window.location.href = url;
        } catch (err: any) {
            console.error(`${provider} Login Error:`, err);
            setError(err.message || `Failed to initiate ${provider} login`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        >
            <motion.div
                initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
                className="bg-slate-900 border border-slate-700 w-full max-w-2xl rounded-3xl shadow-2xl relative flex flex-col max-h-[85vh]"
            >
                <div className="p-6 overflow-y-auto custom-scrollbar">
                    <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white z-10 bg-slate-900/50 rounded-full p-1">
                        <X size={20} />
                    </button>

                    <div className="flex justify-center mb-6">
                        <img src="/logo-v2.webp" alt="PadelPartner" className="w-24 h-24 rounded-3xl shadow-lg" />
                    </div>

                    <h2 className="text-2xl font-bold text-white mb-6 text-center">
                        {mode === 'login' ? t('modal.title_login') : mode === 'register' ? t('modal.title_register') : t('modal.title_admin')}
                    </h2>

                    {error && (
                        <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-xl text-red-200 text-sm text-center">
                            {error}
                        </div>
                    )}

                    <form className="flex flex-col gap-3" onSubmit={(e) => e.preventDefault()}>
                        {mode === 'register' && (
                            <div className="space-y-3">
                                {/* Manager Toggle - Compact */}
                                <div
                                    className={`p-2.5 rounded-xl border transition cursor-pointer flex items-center gap-3 ${isManager ? 'bg-blue-900/30 border-blue-500' : 'bg-slate-800 border-slate-700 hover:border-slate-600'}`}
                                    onClick={() => setIsManager(!isManager)}
                                >
                                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${isManager ? 'border-blue-500 bg-blue-500 text-white' : 'border-slate-500'}`}>
                                        {isManager && <CheckCircle size={10} />}
                                    </div>
                                    <div className="flex-1">
                                        <span className="font-bold text-white block text-sm">{t('register.manager_toggle_title')}</span>
                                    </div>
                                </div>

                                {isManager && (
                                    <div className="flex flex-col gap-2 p-3 bg-slate-800/50 rounded-xl border border-slate-700 animate-in fade-in slide-in-from-top-1">
                                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider px-1">{t('register.manager_type_title')}</p>
                                        <div className="grid grid-cols-2 gap-2">
                                            <button
                                                type="button"
                                                onClick={() => setManagerType('club')}
                                                className={`p-3 rounded-lg border text-left transition ${managerType === 'club' ? 'bg-blue-600/20 border-blue-500' : 'bg-slate-900 border-slate-700 hover:border-slate-600'}`}
                                            >
                                                <div className="flex items-center gap-2 mb-1">
                                                    <Building2 size={14} className={managerType === 'club' ? 'text-blue-400' : 'text-slate-400'} />
                                                    <span className={`text-xs font-bold ${managerType === 'club' ? 'text-white' : 'text-slate-300'}`}>{t('register.manager_type_club')}</span>
                                                </div>
                                                <p className="text-[10px] text-slate-500 leading-tight">{t('register.manager_type_club_subtitle')}</p>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setManagerType('shop')}
                                                className={`p-3 rounded-lg border text-left transition ${managerType === 'shop' ? 'bg-orange-600/20 border-orange-500' : 'bg-slate-900 border-slate-700 hover:border-slate-600'}`}
                                            >
                                                <div className="flex items-center gap-2 mb-1">
                                                    <Globe size={14} className={managerType === 'shop' ? 'text-orange-400' : 'text-slate-400'} />
                                                    <span className={`text-xs font-bold ${managerType === 'shop' ? 'text-white' : 'text-slate-300'}`}>{t('register.manager_type_shop')}</span>
                                                </div>
                                                <p className="text-[10px] text-slate-500 leading-tight">{t('register.manager_type_shop_subtitle')}</p>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    onClose();
                                                    window.location.href = '/federation/onboard';
                                                }}
                                                className={`p-3 rounded-lg border text-left transition ${managerType === 'federation' ? 'bg-purple-600/20 border-purple-500' : 'bg-slate-900 border-slate-700 hover:border-slate-600'} col-span-2`}
                                            >
                                                <div className="flex items-center gap-2 mb-1">
                                                    <Globe size={14} className="text-purple-400" />
                                                    <span className={`text-xs font-bold text-white`}>{t('register.manager_type_federation')}</span>
                                                </div>
                                                <p className="text-[10px] text-slate-500 leading-tight">{t('register.manager_type_federation_subtitle')}</p>
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {isManager && managerType === 'club' && (
                                    <div className="space-y-3 animate-in slide-in-from-top-2">

                                        {!isNewClub ? (
                                            <>
                                                {selectedClub ? (
                                                    <div className="bg-slate-800 border border-blue-500/50 p-3 rounded-xl flex justify-between items-center">
                                                        <div className="overflow-hidden">
                                                            <div className="font-bold text-blue-400 text-sm truncate">{selectedClub.name}</div>
                                                            <div className="text-xs text-slate-500 truncate">{selectedClub.address}</div>
                                                        </div>
                                                        <button type="button" onClick={() => setSelectedClub(null)} className="text-slate-400 hover:text-red-400 ml-2">
                                                            <X size={18} />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowClubSelector(true)}
                                                        className="w-full py-3 border-2 border-dashed border-slate-600 rounded-xl text-slate-400 font-bold hover:bg-slate-800 hover:text-white transition flex items-center justify-center gap-2 text-sm"
                                                    >
                                                        <Building2 size={18} />
                                                        {t('register.search_club_button')}
                                                    </button>
                                                )}

                                                <div className="text-center">
                                                    <button
                                                        type="button"
                                                        className="text-white hover:text-white text-xs underline decoration-blue-500/50 hover:decoration-blue-500"
                                                        onClick={() => { setIsNewClub(true); setSelectedClub(null); }}
                                                    >
                                                        {t('register.club_not_found')}
                                                    </button>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="p-3 bg-slate-800 rounded-xl border border-slate-700 space-y-3 relative">
                                                <button
                                                    type="button"
                                                    onClick={() => setIsNewClub(false)}
                                                    className="absolute top-2 right-2 text-slate-500 hover:text-white"
                                                >
                                                    <X size={16} />
                                                </button>
                                                <p className="text-xs font-bold text-blue-400 uppercase tracking-wide mb-2">{t('register.new_club_title')}</p>

                                                <input
                                                    type="text"
                                                    placeholder={t('register.club_name_placeholder')}
                                                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    value={newClubName} onChange={e => setNewClubName(e.target.value)}
                                                />
                                                <AddressAutocomplete
                                                    value={newClubData.address}
                                                    onChange={(val) => setNewClubData(prev => ({ ...prev, address: val }))}
                                                    onSelect={(data) => setNewClubData(prev => ({
                                                        ...prev,
                                                        address: data.address,
                                                        city: data.city,
                                                        zip: data.zip_code,
                                                        region: data.region,
                                                        country: data.country,
                                                        latitude: data.lat,
                                                        longitude: data.lon
                                                    }))}
                                                    placeholder={t('register.address_placeholder')}
                                                    className="bg-slate-900 border-slate-600 text-white placeholder:text-gray-500"
                                                />
                                                <div className="grid grid-cols-3 gap-2">
                                                    <input
                                                        type="text"
                                                        placeholder={t('register.city_placeholder')}
                                                        className="col-span-1 w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        value={newClubData.city}
                                                        onChange={e => setNewClubData(prev => ({ ...prev, city: e.target.value }))}
                                                    />
                                                    <input
                                                        type="text"
                                                        placeholder={t('register.zip_placeholder')}
                                                        className="col-span-1 w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        value={newClubData.zip}
                                                        onChange={e => setNewClubData(prev => ({ ...prev, zip: e.target.value }))}
                                                    />
                                                    <input
                                                        type="text"
                                                        placeholder={t('register.country_placeholder')}
                                                        className="col-span-1 w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        value={newClubData.country}
                                                        onChange={e => setNewClubData(prev => ({ ...prev, country: e.target.value }))}
                                                    />
                                                </div>
                                                <div className="relative">
                                                    <Globe className="absolute left-3 top-2.5 text-gray-500" size={14} />
                                                    <input
                                                        type="url"
                                                        placeholder={t('register.website_placeholder')}
                                                        className="w-full pl-9 pr-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        value={newClubData.website}
                                                        onChange={e => setNewClubData(prev => ({ ...prev, website: e.target.value }))}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {(selectedClub || isNewClub) && (
                                            <div className="space-y-2">
                                                <label className="text-xs text-slate-400 font-bold uppercase">{t('register.proofs_label')}</label>
                                                {proofs.map((proof, index) => (
                                                    <div key={index} className="flex gap-2">
                                                        <div className="flex-1 space-y-1">
                                                            <input
                                                                type="url"
                                                                placeholder={t('register.url_placeholder')}
                                                                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                value={proof.url}
                                                                onChange={e => {
                                                                    const newProofs = [...proofs];
                                                                    newProofs[index].url = e.target.value;
                                                                    setProofs(newProofs);
                                                                }}
                                                            />
                                                            <input
                                                                type="text"
                                                                placeholder={t('register.description_placeholder')}
                                                                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                value={proof.description}
                                                                onChange={e => {
                                                                    const newProofs = [...proofs];
                                                                    newProofs[index].description = e.target.value;
                                                                    setProofs(newProofs);
                                                                }}
                                                            />
                                                        </div>
                                                        {proofs.length > 1 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => setProofs(proofs.filter((_, i) => i !== index))}
                                                                className="text-red-400 hover:text-red-300 self-start mt-2"
                                                            >
                                                                <X size={16} />
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                                <button
                                                    type="button"
                                                    onClick={() => setProofs([...proofs, { url: '', description: '' }])}
                                                    className="text-xs text-blue-400 hover:underline flex items-center gap-1"
                                                >
                                                    {t('register.add_proof')}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {isManager && managerType === 'shop' && (
                                    <div className="space-y-2 animate-in slide-in-from-top-2">
                                        <div className="relative">
                                            <input
                                                type="text"
                                                placeholder={`${t('register.shop_name_placeholder')} *`}
                                                className={`w-full px-4 py-3 bg-slate-800 border rounded-xl text-white focus:outline-none focus:ring-2 transition-all ${isShopNameAvailable === true ? 'border-green-500 ring-green-500/20' : isShopNameAvailable === false ? 'border-red-500 ring-red-500/20' : 'border-slate-700 focus:ring-blue-500'}`}
                                                value={shopName}
                                                onChange={e => setShopName(e.target.value)}
                                                required
                                            />
                                            <div className="absolute right-3 top-3.5 flex items-center gap-2">
                                                {isCheckingShopName && <Loader2 size={16} className="text-blue-400 animate-spin" />}
                                                {isShopNameAvailable === true && !isCheckingShopName && <CheckCircle size={16} className="text-green-500" />}
                                                {isShopNameAvailable === false && !isCheckingShopName && <AlertCircle size={16} className="text-red-500" />}
                                            </div>
                                        </div>
                                        {shopNameError && (
                                            <p className="text-[10px] text-red-400 font-medium px-1 flex items-center gap-1">
                                                <AlertCircle size={10} />
                                                {shopNameError}
                                            </p>
                                        )}
                                        {isShopNameAvailable === true && (
                                            <p className="text-[10px] text-green-400 font-medium px-1 flex items-center gap-1">
                                                <CheckCircle size={10} />
                                                {t('register.shop_name_available')}
                                            </p>
                                        )}
                                        <AddressAutocomplete
                                            value={shopData.address}
                                            onChange={(val) => setShopData(prev => ({ ...prev, address: val }))}
                                            onSelect={(data) => setShopData(prev => ({
                                                ...prev,
                                                address: data.address,
                                                city: data.city,
                                                zip: data.zip_code,
                                                region: data.region,
                                                country: data.country,
                                                latitude: data.lat,
                                                longitude: data.lon
                                            }))}
                                            placeholder={`${t('register.address_placeholder')} *`}
                                            className="bg-slate-800 border-slate-700 text-white placeholder:text-gray-500"
                                        />
                                        <div className="grid grid-cols-3 gap-2">
                                            <input
                                                type="text"
                                                placeholder={t('register.city_placeholder')}
                                                className="col-span-1 w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                value={shopData.city}
                                                onChange={e => setShopData(prev => ({ ...prev, city: e.target.value }))}
                                            />
                                            <input
                                                type="text"
                                                placeholder={t('register.zip_placeholder')}
                                                className="col-span-1 w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                value={shopData.zip}
                                                onChange={e => setShopData(prev => ({ ...prev, zip: e.target.value }))}
                                            />
                                            <input
                                                type="text"
                                                placeholder={t('register.country_placeholder')}
                                                className="col-span-1 w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                value={shopData.country}
                                                onChange={e => setShopData(prev => ({ ...prev, country: e.target.value }))}
                                            />
                                        </div>
                                        <div className="relative">
                                            <Globe className="absolute left-3 top-2.5 text-slate-500" size={14} />
                                            <input
                                                type="url"
                                                placeholder={t('register.website_placeholder')}
                                                className="w-full pl-9 pr-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                                value={shopData.website_url}
                                                onChange={e => setShopData(prev => ({ ...prev, website_url: e.target.value }))}
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className="grid grid-cols-2 gap-3">
                                    <input
                                        type="text" placeholder={`${t('register.first_name_placeholder')} *`}
                                        className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        value={firstName} onChange={e => setFirstName(e.target.value)}
                                        required
                                    />
                                    <input
                                        type="text" placeholder={`${t('register.last_name_placeholder')} *`}
                                        className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        value={lastName} onChange={e => setLastName(e.target.value)}
                                        required
                                    />
                                </div>

                                {!isManager && (
                                    <div className="relative">
                                        <UserIcon className="absolute left-3 top-3.5 text-slate-400" size={18} />
                                        <input
                                            type="text" placeholder={t('register.pseudo_placeholder')}
                                            className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            value={pseudo} onChange={e => setPseudo(e.target.value)}
                                            minLength={2}
                                            onKeyDown={(e) => e.key === 'Enter' && handleSubmit(e)}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        <input
                            type="email" placeholder={`${t('login.email_label')} *`}
                            className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={email} onChange={e => setEmail(e.target.value)} required
                            onKeyDown={(e) => e.key === 'Enter' && handleSubmit(e)}
                        />
                        <div className="relative">
                            <Lock className="absolute left-3 top-3.5 text-slate-400" size={18} />
                            <input
                                type={showPassword ? "text" : "password"}
                                placeholder={t('register.password_placeholder')}
                                className="w-full pl-10 pr-10 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={password} onChange={e => setPassword(e.target.value)} required
                                minLength={6}
                                onKeyDown={(e) => e.key === 'Enter' && handleSubmit(e)}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-3.5 text-slate-400 hover:text-white focus:outline-none"
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                        {(mode === 'login' || mode === 'admin-login') && (
                            <div className="flex justify-end -mt-1">
                                <a href="/forgot-password" onClick={(e) => { e.preventDefault(); onClose(); window.location.href = '/forgot-password'; }} className="text-sm text-blue-400 hover:text-blue-300 hover:underline">
                                    {t('modal.forgot_password')}
                                </a>
                            </div>
                        )}

                        {mode === 'register' && (
                            <div className="relative">
                                <Lock className="absolute left-3 top-3.5 text-slate-400" size={18} />
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    placeholder={t('register.confirm_password_label')}
                                    className="w-full pl-10 pr-10 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required
                                    minLength={6}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-3 top-3.5 text-slate-400 hover:text-white focus:outline-none"
                                >
                                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        )}

                        {mode === 'register' && (
                            <div className="space-y-3">
                                <div className="flex items-start gap-2 text-sm text-slate-400">
                                    <input
                                        type="checkbox"
                                        id="modal-accept-cgu"
                                        required
                                        className="mt-1 w-4 h-4 rounded border-slate-600 bg-slate-800 text-blue-600 focus:ring-blue-500"
                                        checked={termsAccepted}
                                        onChange={(e) => setTermsAccepted(e.target.checked)}
                                    />
                                    <label htmlFor="modal-accept-cgu">
                                        {t('register.accept_cgu_prefix')} <a href="/cgu?mode=mobile" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">{t('register.accept_cgu_link')}</a>
                                    </label>
                                </div>
                                <div className="flex items-start gap-2 text-sm text-slate-400">
                                    <input
                                        type="checkbox"
                                        id="modal-accept-privacy"
                                        required
                                        className="mt-1 w-4 h-4 rounded border-slate-600 bg-slate-800 text-blue-600 focus:ring-blue-500"
                                        checked={privacyAccepted}
                                        onChange={(e) => setPrivacyAccepted(e.target.checked)}
                                    />
                                    <label htmlFor="modal-accept-privacy">
                                        {t('register.accept_privacy_prefix')} <a href="/privacy?mode=mobile" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{t('register.accept_privacy_link')}</a>
                                    </label>
                                </div>
                            </div>
                        )}

                        <button
                            type="button" // Changed from submit to button to prevent native form submission/reload
                            onClick={handleSubmit}
                            disabled={loading}
                            className="w-full py-3 mt-2 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl shadow-lg transition-all disabled:opacity-50"
                        >
                            {loading ? t('register.submit_loading') : (mode === 'register' ? t('register.submit_button') : t('login.submit_button'))}
                        </button>

                        {(mode === 'login' || mode === 'register' || mode === 'admin-login') && (
                            <>
                                <div className="relative my-2">
                                    <div className="absolute inset-0 flex items-center">
                                        <span className="w-full border-t border-slate-800"></span>
                                    </div>
                                    <div className="relative flex justify-center text-xs uppercase">
                                        <span className="bg-slate-900 px-2 text-slate-500 font-bold">{t('modal.or') || 'Ou'}</span>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    onClick={() => handleSocialLogin('google')}
                                    disabled={loading}
                                    className="w-full py-3 bg-white hover:bg-slate-100 text-slate-900 font-bold rounded-xl shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    <svg viewBox="0 0 24 24" className="w-5 h-5">
                                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-1 .67-2.28 1.07-3.71 1.07-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                                    </svg>
                                    {t('login.login_with_google')}
                                </button>

                                <button
                                    type="button"
                                    onClick={() => handleSocialLogin('outlook')}
                                    disabled={loading}
                                    className="w-full py-3 bg-white hover:bg-slate-100 text-slate-900 font-bold rounded-xl shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    <svg viewBox="0 0 21 21" className="w-5 h-5">
                                        <rect x="0" y="0" width="10" height="10" fill="#f25022" />
                                        <rect x="11" y="0" width="10" height="10" fill="#7fba00" />
                                        <rect x="0" y="11" width="10" height="10" fill="#00a4ef" />
                                        <rect x="11" y="11" width="10" height="10" fill="#ffb900" />
                                    </svg>
                                    {t('login.login_with_outlook')}
                                </button>

                                <button
                                    type="button"
                                    onClick={() => handleSocialLogin('facebook')}
                                    disabled={loading}
                                    className="w-full py-3 bg-[#1877F2] hover:bg-[#166FE5] text-white font-bold rounded-xl shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.469h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.469h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                    </svg>
                                    {t('login.login_with_facebook') || 'Continuer avec Facebook'}
                                </button>

                                <button
                                    type="button"
                                    onClick={handlePasskeyLogin}
                                    disabled={loading}
                                    className="w-full py-3 bg-slate-800 border border-slate-700 hover:bg-slate-700 text-white font-bold rounded-xl shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    <Fingerprint size={20} className="text-blue-400" />
                                    {t('login.login_with_passkey')}
                                </button>
                            </>
                        )}

                    </form>

                    {mode !== 'admin-login' && (
                        <p className="mt-6 text-center text-sm text-slate-400">
                            {mode === 'login' ? t('login.no_account') : t('register.already_registered')}
                            <button onClick={() => setMode(mode === 'login' ? 'register' : 'login')} className="ml-2 text-yellow-400 hover:underline">
                                {mode === 'login' ? t('login.register_link') : t('register.login_link')}
                            </button>
                        </p>
                    )}

                    <div className="mt-6 border-t border-slate-800 pt-4 text-center">
                        <p className="text-[10px] text-slate-500">
                            {t('modal.footer_prefix')}
                            <a href="/cgu?mode=mobile" className="text-slate-400 hover:text-white hover:underline mx-1">{t('register.accept_cgu_link')}</a>
                            {t('modal.footer_and')}
                            <a href="/privacy?mode=mobile" className="text-slate-400 hover:text-white hover:underline mx-1">{t('register.accept_privacy_link')}</a>.
                        </p>
                    </div>
                </div>
            </motion.div>

            {showClubSelector && (
                <ClubSelector
                    clubs={clubs.length > 0 ? clubs : internalClubs}
                    onClose={() => setShowClubSelector(false)}
                    onSelect={(club) => {
                        setSelectedClub(club);
                        setShowClubSelector(false);
                    }}
                />
            )}
        </motion.div>
    );
};

export default AuthModal;
